import { create } from 'zustand';
import type { Scene } from '@/types';

export type InputMode = 'single' | 'batch';
export type AssetMode = 'image' | 'video';

interface References {
  sceneIds: string[];
  castIds: string[];
}

interface SelectionStore {
  activeSceneId: string | null;
  pendingReferences: References;
  inputMode: InputMode;
  assetMode: AssetMode;          // image vs video — only meaningful on the scene detail page
  freshPrompt: boolean;
  rawPrompt: string;

  // Coarse-grained — used by undo to snapshot/restore
  snapshot: () => SelectionSnapshot;
  restore: (snap: SelectionSnapshot) => void;

  // Active scene management
  activateScene: (
    scene: Scene | null,
    opts?: { fresh?: boolean; prompt?: string; references?: References },
  ) => void;
  setRawPrompt: (s: string) => void;
  setFreshPrompt: (on: boolean) => void;
  setInputMode: (mode: InputMode) => void;
  setAssetMode: (mode: AssetMode) => void;

  // Reference toggles — scene refs and cast refs
  toggleReferenceScene: (id: string) => void;
  toggleReferenceCast: (id: string) => void;
  removeReferenceScene: (id: string) => void;
  removeReferenceCast: (id: string) => void;
  clearReferences: () => void;
  setReferences: (refs: References) => void;
}

export interface SelectionSnapshot {
  activeSceneId: string | null;
  pendingReferences: References;
  freshPrompt: boolean;
}

const EMPTY_REFS: References = { sceneIds: [], castIds: [] };

export const useSelectionStore = create<SelectionStore>((set, get) => ({
  activeSceneId: null,
  pendingReferences: EMPTY_REFS,
  inputMode: 'single',
  assetMode: 'image',
  freshPrompt: false,
  rawPrompt: '',

  snapshot: () => {
    const s = get();
    return {
      activeSceneId: s.activeSceneId,
      pendingReferences: {
        sceneIds: [...s.pendingReferences.sceneIds],
        castIds: [...s.pendingReferences.castIds],
      },
      freshPrompt: s.freshPrompt,
    };
  },
  restore: (snap) =>
    set({
      activeSceneId: snap.activeSceneId,
      pendingReferences: {
        sceneIds: [...snap.pendingReferences.sceneIds],
        castIds: [...snap.pendingReferences.castIds],
      },
      freshPrompt: snap.freshPrompt,
    }),

  activateScene: (scene, opts) =>
    set(() => {
      if (!scene) {
        return {
          activeSceneId: null,
          pendingReferences: EMPTY_REFS,
          rawPrompt: '',
          freshPrompt: false,
        };
      }
      return {
        activeSceneId: scene.id,
        pendingReferences: opts?.references ?? scene.references,
        rawPrompt: opts?.prompt ?? scene.prompt,
        freshPrompt: opts?.fresh ?? false,
      };
    }),

  setRawPrompt: (rawPrompt) => set({ rawPrompt }),
  setFreshPrompt: (freshPrompt) => set({ freshPrompt }),
  setInputMode: (inputMode) => set({ inputMode }),
  setAssetMode: (assetMode) => set({ assetMode }),

  toggleReferenceScene: (id) =>
    set((s) => ({
      pendingReferences: {
        sceneIds: s.pendingReferences.sceneIds.includes(id)
          ? s.pendingReferences.sceneIds.filter((x) => x !== id)
          : [...s.pendingReferences.sceneIds, id],
        castIds: s.pendingReferences.castIds,
      },
    })),

  toggleReferenceCast: (id) =>
    set((s) => ({
      pendingReferences: {
        sceneIds: s.pendingReferences.sceneIds,
        castIds: s.pendingReferences.castIds.includes(id)
          ? s.pendingReferences.castIds.filter((x) => x !== id)
          : [...s.pendingReferences.castIds, id],
      },
    })),

  removeReferenceScene: (id) =>
    set((s) => ({
      pendingReferences: {
        sceneIds: s.pendingReferences.sceneIds.filter((x) => x !== id),
        castIds: s.pendingReferences.castIds,
      },
    })),

  removeReferenceCast: (id) =>
    set((s) => ({
      pendingReferences: {
        sceneIds: s.pendingReferences.sceneIds,
        castIds: s.pendingReferences.castIds.filter((x) => x !== id),
      },
    })),

  clearReferences: () => set({ pendingReferences: EMPTY_REFS }),
  setReferences: (refs) => set({ pendingReferences: refs }),
}));
